import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Link } from "react-router-dom";

// Your actual projects (sorted newest to oldest)
const projectsData = [
  {
    id: 4,
    title: "Faltas Lab Slackbot",
    description: "Comprehensive Slack bot for handling laboratory purchase requests with historical data extraction and automated workflow management",
    impact: "Streamlined lab purchase processes with automated request handling and data tracking",
    metrics: { requests: "500+", labs: "Multiple", efficiency: "+60%", since: "October 2024" },
    tech: ["Python", "Flask", "Slack API", "ngrok", "Data Processing", "MIT License"],
    category: "Backend",
    status: "live",
    demoUrl: null,
    githubUrl: "https://github.com/pyoos/Faltas-Slackbot",
    features: [
      "Automated purchase request handling",
      "Historical data extraction",
      "Flask server with ngrok tunneling",
      "Slash command integration",
      "Laboratory workflow automation",
      "Real-time request processing"
    ],
    challenges: "Building a robust system for laboratory purchase workflows, integrating with Slack's event system, and handling real-time data processing",
    solution: "Flask-based architecture with ngrok for local development, comprehensive slash command system, and automated data extraction pipelines"
  },
  {
    id: 3,
    title: "Faltas Lab GMS",
    description: "Grant Management System for the Faltas laboratory - tracking lab spending allocated to specific grants with comprehensive financial oversight",
    impact: "Streamlined laboratory grant management with precise spending allocation and tracking",
    metrics: { grants: "Multiple", tracking: "Real-time", accuracy: "99%", since: "July 2024" },
    tech: ["HTML", "TeX", "Python", "Jupyter Notebook", "Cross-platform Scripts"],
    category: "Full-stack",
    status: "live",
    demoUrl: null,
    githubUrl: "https://github.com/pyoos/Faltas_GMS",
    features: [
      "Grant spending allocation tracking",
      "Laboratory financial oversight",
      "Cross-platform compatibility (Mac/Windows)",
      "Automated reporting system",
      "Historical spending analysis",
      "Multi-grant budget management"
    ],
    challenges: "Building a reliable system for academic grant management, ensuring accurate financial tracking, and providing cross-platform compatibility",
    solution: "Multi-language approach with Python backend, HTML frontend, and platform-specific scripts for seamless deployment across laboratory environments"
  },
  {
    id: 5,
    title: "DDS70",
    description: "Computer vision sports analytics system using YOLOv8 for real-time basketball player tracking, shot analysis, and performance statistics extraction",
    impact: "Advanced sports analytics with real-time object detection and statistical analysis",
    metrics: { models: "YOLOv8", datasets: "10K+", accuracy: "High", since: "October 2023" },
    tech: ["Python", "YOLOv8", "OpenCV", "Jupyter Notebook", "Roboflow", "Flask", "Google Drive API"],
    category: "AI/ML",
    status: "complete",
    demoUrl: null,
    githubUrl: "https://github.com/pyoos/DoesDarrenShootLessThan70-",
    features: [
      "Real-time video object tracking",
      "YOLOv8 model training on 10K dataset",
      "Basketball player detection and segmentation",
      "Statistical analysis extraction",
      "Web interface for video upload",
      "Google Drive API integration"
    ],
    challenges: "Building accurate real-time sports tracking, training custom models on basketball-specific datasets, and creating seamless video processing pipeline",
    solution: "YOLOv8 implementation with Roboflow dataset management, Flask backend with real-time webcam integration, and comprehensive video analysis pipeline"
  },
  {
    id: 6,
    title: "UNet Single Cell Segmentation",
    description: "Biomedical AI system using UNet deep learning architecture for precise single cell segmentation in microscopy images",
    impact: "Advanced biomedical image analysis with deep learning-powered cell identification and segmentation",
    metrics: { model: "UNet", accuracy: "High", cells: "1000s", since: "January 2022" },
    tech: ["Python", "TensorFlow/Keras", "UNet", "Jupyter Notebook", "OpenCV", "NumPy", "Matplotlib"],
    category: "AI/ML",
    status: "complete",
    demoUrl: null,
    githubUrl: "https://github.com/pyoos/UNet_SingleCellSegmentation",
    features: [
      "UNet deep learning architecture",
      "Single cell microscopy segmentation",
      "Biomedical image preprocessing",
      "High-precision cell boundary detection",
      "Research-grade analysis pipeline",
      "MIT licensed open-source"
    ],
    challenges: "Implementing accurate cell segmentation on complex microscopy images, handling variable cell shapes and sizes, and achieving research-grade precision",
    solution: "UNet convolutional neural network with encoder-decoder architecture, specialized for biomedical image segmentation with data augmentation and preprocessing pipeline"
  },
  {
    id: 1,
    title: "Wave Dating App Website",
    description: "Performance-first marketing website with custom Node.js build system, advanced animations, and video-optimized architecture",
    impact: "Lightning-fast static site with rich interactions - faster than typical React SPAs",
    metrics: { visitors: "Active", conversion: "High", uptime: "99.9%", since: "March 2020" },
    tech: ["Node.js", "Pug", "SCSS", "GSAP", "Bootstrap 5", "Swiper", "Lottie", "PostCSS"],
    category: "Frontend",
    status: "live",
    demoUrl: "http://wavedatingapp.com/index.html",
    githubUrl: "https://github.com/username/wave-website",
    features: [
      "Custom Node.js build system", 
      "GSAP ScrollTrigger animations", 
      "Video-first performance optimization", 
      "Pug templating with modular components"
    ],
    challenges: "Building a modern, animation-rich marketing site without SPA overhead while maintaining developer experience and performance",
    solution: "Custom build pipeline with Pug→HTML, SCSS→PostCSS, GSAP for motion, and performance-optimized video loading strategies"
  },
  {
    id: 2,
    title: "Wave Dating App",
    description: "TikTok meets Tinder - A revolutionary video-first dating platform where users swipe on authentic videos instead of photos",
    impact: "1.3K+ downloads with 4.9 App Store rating",
    metrics: { downloads: "1.3K+", rating: "4.9/5", users: "Active", since: "March 2020" },
    tech: ["React Native", "Node.js", "Firebase", "Video Processing", "iOS", "Android"],
    category: "Mobile",
    status: "live",
    demoUrl: "https://apps.apple.com/app/wave-dating",
    githubUrl: "https://github.com/username/wave-dating-app",
    features: [
      "30-second video profiles", 
      "Video-first matching system", 
      "Live video dates", 
      "Location-based discovery",
      "Voice & video stories",
      "Real-time chat",
      "Push notifications",
      "In-app purchases"
    ],
    challenges: "Creating smooth video upload/playbook experience while maintaining app performance, building authentic user engagement, and scaling video infrastructure",
    solution: "Implemented optimized video compression, CDN delivery, intuitive UX for video creation, and scalable backend architecture with real-time features"
  }
];



const categories = ["All", "Full-stack", "Frontend", "Backend", "Mobile", "AI/ML"];

export default function Projects() {
  const [selectedCategory, setSelectedCategory] = useState("All");
  const [searchTerm, setSearchTerm] = useState("");
  const [collapsedCards, setCollapsedCards] = useState({});

  // Filter projects based on category and search
  const filteredProjects = projectsData.filter(project => {
    const matchesCategory = selectedCategory === "All" || project.category === selectedCategory;
    const matchesSearch = project.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         project.description.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         project.tech.some(tech => tech.toLowerCase().includes(searchTerm.toLowerCase()));
    return matchesCategory && matchesSearch;
  });

  useEffect(() => {
    const handleContextMenu = e => e.preventDefault();
    document.addEventListener("contextmenu", handleContextMenu);
    const handleKeyDown = e => {
      if (
        e.key === "F12" ||
        (e.ctrlKey && e.shiftKey && (e.key === "I" || e.key === "C" || e.key === "J")) ||
        (e.metaKey && e.altKey && e.key.toLowerCase() === "i") ||
        (e.ctrlKey && e.key.toLowerCase() === "u")
      ) {
        e.preventDefault();
      }
    };
    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.removeEventListener("contextmenu", handleContextMenu);
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, []);



     const StatusBadge = ({ status }) => {
     const statusConfig = {
       live: { color: "bg-success text-success-foreground", text: "🟢 Live" },
       development: { color: "bg-warning text-warning-foreground", text: "🟡 In Development" },
       archived: { color: "bg-muted text-muted-foreground", text: "📦 Archived" }
     };
     
     const config = statusConfig[status] || statusConfig.archived;
     
     return (
       <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${config.color}`}>
         {config.text}
       </span>
     );
   };

        const toggleCard = (projectId) => {
     setCollapsedCards(prev => ({
       ...prev,
       [projectId]: !prev[projectId]
     }));
   };

   const ProjectCard = ({ project }) => {
     const isCollapsed = collapsedCards[project.id];
     
     return (
             <div className="bg-card border-border rounded-lg hover:shadow-md transition-all duration-300 shadow-sm">
         {/* Project Content */}
         <div className="p-6">
           {/* Header with Collapse Button */}
           <div className="flex justify-between items-start mb-4">
             <div className="flex-1">
               <div className="flex items-center justify-between mb-2">
                 <h3 className="text-xl font-semibold text-card-foreground">{project.title}</h3>
                 <button
                   onClick={() => toggleCard(project.id)}
                   className="text-muted-foreground hover:text-foreground transition-colors ml-4"
                   aria-label={isCollapsed ? "Expand project" : "Collapse project"}
                 >
                   <svg className={`w-5 h-5 transition-transform ${isCollapsed ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                     <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                   </svg>
                 </button>
               </div>
               
                             <div className="flex items-center justify-between text-sm text-muted-foreground mb-3">
                <span>{project.metrics.since}</span>
                 <div className="flex items-center gap-3">
                   <span className="bg-secondary text-secondary-foreground px-2 py-1 rounded text-xs font-medium">
                     {project.category}
                   </span>
                   <StatusBadge status={project.status} />
                 </div>
               </div>
             </div>
           </div>

           {/* Description - Always Visible */}
           <p className="text-muted-foreground mb-4 leading-relaxed">{project.description}</p>

                     {/* Collapsible Content */}
          <div className={`overflow-hidden transition-all duration-300 ease-in-out ${
            isCollapsed ? 'max-h-0 opacity-0' : 'max-h-[1000px] opacity-100'
          }`}>
                         <div className="space-y-4 mb-6 pt-1">
                {/* Tech Stack */}
                 <div>
                   <h4 className="text-sm font-semibold text-card-foreground mb-2">Tech Stack</h4>
                   <div className="flex flex-wrap gap-2">
                     {project.tech.map((tech) => (
                       <span
                         key={tech}
                         className="bg-muted text-muted-foreground px-2 py-1 rounded text-xs font-medium"
                       >
                         {tech}
                       </span>
                     ))}
                   </div>
                 </div>

                 {/* Key Features */}
                 <div>
                   <h4 className="text-sm font-semibold text-card-foreground mb-2">Key Features</h4>
                   <div className="grid grid-cols-1 gap-1">
                     {project.features.slice(0, 4).map((feature, index) => (
                       <div key={index} className="text-muted-foreground text-sm flex items-center">
                         <span className="text-primary mr-2">•</span>
                         {feature}
                       </div>
                     ))}
                   </div>
                 </div>
               </div>
             </div>

                         {/* Actions - Always Visible */}
            <div className="flex gap-3 pt-4 border-t border-border">
              {project.demoUrl && (
                <a
                  href={project.demoUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-primary hover:text-primary/80 font-medium text-sm transition-colors"
                >
                  Visit Website
                </a>
              )}
                             {project.title === "DDS70" && (
                 <Link
                   to="/DDS70"
                   className="text-primary hover:text-primary/80 font-medium text-sm transition-colors"
                 >
                   Test Demo
                 </Link>
               )}
              <a
                href={project.githubUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="text-muted-foreground hover:text-foreground font-medium text-sm transition-colors"
              >
                Visit GitHub
              </a>
            </div>
         </div>
       </div>
     );
   };

  return (
    <div className="min-h-screen transition-colors" style={{ backgroundColor: 'inherit' }}>
      <div className="max-w-4xl mx-auto px-4 py-8">
        {/* Header */}
        <div className="mb-12">
          <h1 className="text-4xl font-bold mb-4" style={{ color: '#f6e05e' }}>
            Projects
          </h1>
          <p className="text-lg text-muted-foreground max-w-2xl">
            I have worked on a variety of projects over the years; some of them as a hobby, some as a proof of concept and others to solve my own pain points. Here are some of the projects that I have worked on.
          </p>
        </div>

        {/* Search and Filters */}
        <div className="mb-8 space-y-4">
          {/* Search */}
          <div className="relative max-w-md">
            <input
              type="text"
              placeholder="Search projects, technologies..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border-border rounded-lg text-card-foreground focus:ring-2 focus:ring-ring focus:border-ring outline-none transition-colors bg-card"
            />
            <svg className="absolute left-3 top-2.5 h-5 w-5 text-muted-foreground" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
            </svg>
          </div>

          {/* Category Filters */}
          <div className="flex flex-wrap gap-2">
            {categories.map((category) => (
              <button
                key={category}
                onClick={() => setSelectedCategory(category)}
                className={`px-3 py-1 rounded-full text-sm font-medium transition-all ${
                  selectedCategory === category
                    ? "bg-primary text-primary-foreground"
                    : "text-card-foreground hover:bg-accent hover:text-accent-foreground bg-card"
                }`}
              >
                {category}
              </button>
            ))}
          </div>
        </div>

        {/* Projects Grid */}
        <div className="space-y-6">
          <AnimatePresence mode="wait">
            {filteredProjects.map((project, index) => (
              <motion.div
                key={`${selectedCategory}-${project.id}`}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
                transition={{ duration: 0.2, delay: index * 0.05, ease: "easeOut" }}
                className="transform transition-transform duration-200 hover:scale-[1.01]"
              >
                <ProjectCard project={project} />
              </motion.div>
            ))}
          </AnimatePresence>
        </div>

        {/* No Results */}
        {filteredProjects.length === 0 && (
          <div className="text-center py-12">
            <p className="text-muted-foreground text-lg">
              No projects found matching your criteria.
            </p>
          </div>
        )}

        
      </div>
    </div>
  );
} 